import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import { mainStore } from 'common/clients/jotai-store.client.ts';
import { queryClient } from 'common/clients/query.client.ts';
import { API_ENDPOINTS } from 'common/constants/api-endpoints.constants.ts';
import { EUrlQueryParams } from 'common/enums/url-query-params.enums.ts';
import { profileConfigAtom } from 'common/stores/profile-config.store.ts';
import { captureError, IError } from 'common/utils/error.utils.ts';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants.ts';
import routerTree from 'navigation/routerTree.tsx';

export interface IReqOptions {
  from?: string,
}

interface IInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  from?: string;
}

export const apiClient = axios.create({
  baseURL: API_ENDPOINTS.BASE_URL,
  withCredentials: true,
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { indices: false }),
  },
});

const apiClientInterceptorsReq = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => ({
  ...config,
});

const apiClientInterceptorsReqError = async (error: AxiosError): Promise<AxiosError> => Promise.reject(error);

const apiClientInterceptorsRes = (response: AxiosResponse): AxiosResponse => response;

const apiClientInterceptorsResError = async (error: AxiosError): Promise<AxiosError> => {
  const { response } = error;
  const searchParams = new URLSearchParams();
  const config = error.config as IInternalAxiosRequestConfig;
  const responseData = response?.data as { error?: unknown } | undefined;
  const rawErrorData = responseData?.error;
  const errorData = typeof rawErrorData === 'string' ? rawErrorData : JSON.stringify(rawErrorData ?? '');

  if (response?.status === 401) {
    const from = config.from ?? window.location.pathname;
    searchParams.append(EUrlQueryParams.ReturnTo, from);

    mainStore.set(profileConfigAtom, () => ({
      userIsAuthorized: false,
    }));
    queryClient.clear();

    await routerTree.navigate(`${ROUTES_MAPPING.PUBLIC.LOGIN}?${searchParams.toString()}`);
  }

  // Handle all 400 and all 500 errors
  if (
    (response?.status ?? 0) >= 400
    // Exception for interactive flow
    && !(
      response?.status === 409
      && response.config.method === 'post'
      && (
        response.config.url === API_ENDPOINTS.ON_OFF_RAMP.ON_RAMP
        || response.config.url === API_ENDPOINTS.ON_OFF_RAMP.OFF_RAMP
      )
    )
    // Exception for billing field 'Country if residence'
    && !(
      response?.status === 400
      && response.config.method === 'put'
      && response.config.url === API_ENDPOINTS.USER.BILLING_FIELDS
    )
    // Exception for 401
    && response?.status !== 401
  ) {
    if (response?.status === 404) {
      await routerTree.navigate(ROUTES_MAPPING.NO_MATCH);
    } else if (
      // response?.status === 500
      // && response.config.method === 'get'
      // && config.url === API_ENDPOINTS.ON_OFF_RAMP.ORDER_FLOW
      // eslint-disable-next-line max-len
      errorData.includes('failed to get order URL: can\'t create fpf order url: failed to get order URL: response status code is not 2xx but 500;')
        || errorData.includes('no payment methods found')
    ) {
      captureError(error as IError);
      await routerTree.navigate(ROUTES_MAPPING.PAYMENT_OPTIONS_UNAVAILABLE);
    } else {
      captureError(error as IError);
      await routerTree.navigate(ROUTES_MAPPING.ERROR);
    }
  }

  return Promise.reject(error);
};

apiClient.interceptors.request.use(apiClientInterceptorsReq, apiClientInterceptorsReqError);
apiClient.interceptors.response.use(apiClientInterceptorsRes, apiClientInterceptorsResError);
